// =====================================================================
//  ALTA Y EDICIÓN DE HITOS — solo máster
//
//  El cronograma no es una lista fija. Entre agosto y septiembre pueden
//  aparecer incidencias procesales —una suspensión, una reposición, un
//  cambio de fecha— y hay que poder registrarlas sin tocar el código.
// =====================================================================

import { useState } from 'react'
import { useBorrarHito, useCrearHito, useGuardarHito } from '../datos/consultas'
import { Campo, Fila2, TituloCard, useAvisar } from '../componentes/ui'
import { fechaISO } from '../dominio/formato'
import type { Hito } from '../tipos/dominio'

export function AgregarHito({ causaId }: { causaId: number }) {
  const crear = useCrearHito()
  const avisar = useAvisar()
  const [abierto, setAbierto] = useState(false)
  const [f, setF] = useState({ fecha: '', titulo: '', detalle: '', articulo: '' })

  const aplicar = async () => {
    if (!f.fecha || !f.titulo.trim()) {
      avisar(new Error('Hacen falta al menos la fecha y el título.'))
      return
    }
    try {
      await crear.mutateAsync({
        causa_id: causaId,
        fecha: f.fecha,
        titulo: f.titulo.trim(),
        detalle: f.detalle.trim() || null,
        articulo: f.articulo.trim() || null,
        // Al final del día que le toque; el orden fino casi nunca importa.
        orden: 50,
      })
      setF({ fecha: '', titulo: '', detalle: '', articulo: '' })
      setAbierto(false)
    } catch (e) {
      avisar(e)
    }
  }

  if (!abierto) {
    return (
      <div style={{ marginTop: 14 }}>
        <button className="btn" onClick={() => setAbierto(true)}>
          Agregar un hito
        </button>
        <span className="sub" style={{ marginLeft: 10 }}>
          Para registrar incidencias procesales: suspensiones, reposiciones, cambios de fecha.
        </span>
      </div>
    )
  }

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <TituloCard>Hito nuevo</TituloCard>
      <Fila2>
        <Campo etiqueta="Fecha">
          <input type="date" value={f.fecha} onChange={(e) => setF({ ...f, fecha: e.target.value })} />
        </Campo>
        <Campo etiqueta="Artículo (opcional)">
          <input
            value={f.articulo}
            placeholder="art. 147"
            onChange={(e) => setF({ ...f, articulo: e.target.value })}
          />
        </Campo>
      </Fila2>
      <Campo etiqueta="Título">
        <input
          value={f.titulo}
          placeholder="Se suspende la audiencia"
          onChange={(e) => setF({ ...f, titulo: e.target.value })}
        />
      </Campo>
      <Campo etiqueta="Detalle (opcional)">
        <textarea value={f.detalle} onChange={(e) => setF({ ...f, detalle: e.target.value })} />
      </Campo>
      <div style={{ display: 'flex', gap: 9 }}>
        <button className="btn pri" disabled={crear.isPending} onClick={() => void aplicar()}>
          {crear.isPending ? 'Guardando…' : 'Agregar'}
        </button>
        <button className="btn" onClick={() => setAbierto(false)}>
          Cancelar
        </button>
      </div>
    </div>
  )
}

/** Los controles que aparecen sobre cada hito cuando el máster los edita. */
export function ControlesHito({ hito }: { hito: Hito }) {
  const guardar = useGuardarHito()
  const borrar = useBorrarHito()
  const avisar = useAvisar()
  const [editando, setEditando] = useState(false)
  const [f, setF] = useState({
    fecha: fechaISO(hito.fecha),
    titulo: hito.titulo,
    detalle: hito.detalle ?? '',
    articulo: hito.articulo ?? '',
  })

  const aplicar = async () => {
    try {
      await guardar.mutateAsync({
        id: hito.id,
        cambio: {
          fecha: f.fecha,
          titulo: f.titulo.trim(),
          detalle: f.detalle.trim() || null,
          articulo: f.articulo.trim() || null,
        },
      })
      setEditando(false)
    } catch (e) {
      avisar(e)
    }
  }

  const eliminar = async () => {
    if (!window.confirm(`¿Eliminar el hito «${hito.titulo}»?`)) return
    try {
      await borrar.mutateAsync(hito.id)
    } catch (e) {
      avisar(e)
    }
  }

  if (!editando) {
    return (
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <button
          className="btn sm"
          style={{ padding: '2px 8px', fontSize: 11 }}
          onClick={() => setEditando(true)}
        >
          editar
        </button>
        <button
          className="btn sm"
          style={{ padding: '2px 8px', fontSize: 11, color: 'var(--bad)', borderColor: '#5c2530' }}
          onClick={() => void eliminar()}
        >
          eliminar
        </button>
      </div>
    )
  }

  return (
    <div className="card" style={{ marginTop: 8, background: 'var(--panel2)' }}>
      <Fila2>
        <Campo etiqueta="Fecha">
          <input type="date" value={f.fecha} onChange={(e) => setF({ ...f, fecha: e.target.value })} />
        </Campo>
        <Campo etiqueta="Artículo">
          <input value={f.articulo} onChange={(e) => setF({ ...f, articulo: e.target.value })} />
        </Campo>
      </Fila2>
      <Campo etiqueta="Título">
        <input value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })} />
      </Campo>
      <Campo etiqueta="Detalle">
        <textarea value={f.detalle} onChange={(e) => setF({ ...f, detalle: e.target.value })} />
      </Campo>
      <div style={{ display: 'flex', gap: 7 }}>
        <button className="btn sm pri" onClick={() => void aplicar()}>
          Guardar
        </button>
        <button className="btn sm" onClick={() => setEditando(false)}>
          Cancelar
        </button>
      </div>
    </div>
  )
}
